import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  CircularProgress,
  Box,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import { StyledButton } from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";

export default function ConnectorPaymentsDialog({
  open,
  onClose,
  startDate,
  endDate,
  dsaId,
  user,
}) {
  const gridRef = useRef(null); // AG Grid ref for accurate filtered export

  const [rowData, setRowData] = useState([]);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const jwt_token = Cookie.get("jwt_token");

  const formatIndianCurrency = (value) => {
    if (value == null) return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Define all columns
  const allColumnDefs = useMemo(
    () => [
      {
        headerName: "Remarks",
        field: "remarks",
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        headerName: "Amount",
        field: "amount",
        sortable: true,
        filter: true,
        valueFormatter: (params) => formatIndianCurrency(params.value),
        cellStyle: (params) => ({
          textAlign: "right",
          fontWeight:
            params.data?.remarks === "Total" || params.data?.remarks === "Total (Filtered)"
              ? "bold"
              : "500",
        }),
        flex: 1,
        type: "numericColumn",
      },
      {
        headerName: "TransactionDate",
        field: "tranDate",
        sortable: true,
        filter: true,
      },
      {
        headerName: "ConnectorAccName",
        field: "connectorActNames",
        sortable: true,
        filter: true,
      },
      {
        headerName: "KHAccountName",
        field: "khsacctNames",
        sortable: true,
        filter: true,
      },
    ],
    []
  );

  // Initialize column options for search
  useEffect(() => {
    const options = allColumnDefs
      .filter((col) => col.field)
      .map((col) => ({
        label: col.headerName,
        value: col.field,
        field: col.field,
        type: col.type || "string",
      }));
    setColumnOptions(options);

    if (options.length > 0 && !searchColumn) {
      setSearchColumn(options[0]);
    }
  }, [allColumnDefs, searchColumn]);

  // Filter data based on custom search
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredRowData(rowData);
      return;
    }

    const lowerSearchText = searchText.toLowerCase();
    const filtered = rowData.filter((row) => {
      const fieldValue = row[searchColumn.field];
      if (fieldValue == null) return false;

      if (searchColumn.type === "numericColumn" || typeof fieldValue === "number") {
        return fieldValue.toString().includes(searchText);
      }

      return fieldValue.toString().toLowerCase().includes(lowerSearchText);
    });

    setFilteredRowData(filtered);
  }, [rowData, searchColumn, searchText]);

  // Calculate footer totals
  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0) {
      return { amount: 0 };
    }
    return {
      amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
    };
  }, []);

  // Update pinned bottom row
  useEffect(() => {
    const dataToUse = searchText && searchColumn ? filteredRowData : rowData;
    const totals = calculateFooterTotals(dataToUse);

    const footerRow = {
      remarks: searchText && searchColumn ? "Total (Filtered)" : "Total",
      amount: totals.amount,
      tranDate: "",
      connectorActNames: "",
      khsacctNames: "",
    };

    setPinnedBottomRowData([footerRow]);
  }, [rowData, filteredRowData, searchText, searchColumn, calculateFooterTotals]);

  const handleSearchColumnChange = (event, newValue) => {
    setSearchColumn(newValue);
    if (!newValue) {
      setSearchText("");
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  // ==================== IMPROVED DOWNLOAD EXCEL (Using Grid API) ====================
  const handleDownloadExcel = () => {
    let dataToExport = [];

    // Prefer AG Grid API to get rows after filter (most accurate)
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        if (node.data) {
          dataToExport.push(node.data);
        }
      });
    } else {
      // Fallback
      dataToExport = searchText && searchColumn ? filteredRowData : rowData;
    }

    if (!dataToExport || dataToExport.length === 0) {
      alert("No data available to export");
      return;
    }

    // Recalculate totals based on rows being exported
    const filteredTotals = calculateFooterTotals(dataToExport);

    const filteredFooterRow = {
      remarks: searchText && searchColumn ? "Total (Filtered)" : "Total",
      amount: filteredTotals.amount,
      tranDate: "",
      connectorActNames: "",
      khsacctNames: "",
    };

    const allData = [...dataToExport, filteredFooterRow];

    // Prepare data for Excel
    const exportData = allData.map((row) => ({
      Remarks: row.remarks || "",
      Amount: row.amount || 0,
      TranDate: row.tranDate || "",
      ConnectorActNames: row.connectorActNames || "",
      KhsacctNames: row.khsacctNames || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Connector Payments");

    // Auto-size columns
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 3 }));

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Connector_Payments_${startDate}_to_${endDate}.xlsx`
    );
  };
  // =====================================================================

  // Fetch data
  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const url = `${URLs.connectorBrekdownUrl}?startDate=${startDate}&endDate=${endDate}&dsaId=${dsaId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        });

        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();

        setRowData(data || []);
        setFilteredRowData(data || []);
      } catch (error) {
        console.error("Error fetching connector payments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, startDate, endDate, dsaId, jwt_token]);
  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#f4f6f8",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Top Bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          flexShrink: 0,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            color: "#1F2937",
            borderColor: "#1F2937",
            "&:hover": {
              backgroundColor: "#f3f4f6",
              borderColor: "#111827",
            },
          }}
        >
          Back
        </Button>

        <Typography variant="h6" fontWeight={700} color="#1F2937">
          Connector Payments
        </Typography>

        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleDownloadExcel}
          disabled={rowData.length === 0}
          sx={{ textTransform: "none" }}
        >
          Download Excel
        </StyledButton>
      </Box>

      {/* ── Filter Section ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#fff",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#333">
              Filters
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleClearSearch}
              disabled={!searchText}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Clear Search
            </Button>
          </Stack>

          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={columnOptions}
                getOptionLabel={(option) => option.label}
                value={searchColumn}
                onChange={handleSearchColumnChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Column"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                disableClearable={false}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label={searchColumn ? `Search in "${searchColumn.label}"` : "Search value"}
                variant="outlined"
                size="small"
                value={searchText}
                onChange={handleSearchTextChange}
                disabled={!searchColumn}
                placeholder={
                  searchColumn ? `Type to filter ${searchColumn.label}...` : "Select a column first"
                }
              />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: "block" }}>
            {searchText && searchColumn ? (
              <>
                Showing <strong>{filteredRowData.length}</strong> of{" "}
                <strong>{rowData.length}</strong> rows &nbsp;|&nbsp;
                <span style={{ color: "#1976d2" }}>
                  {searchColumn.label} = &quot;{searchText}&quot;
                </span>
              </>
            ) : (
              <>
                Total <strong>{rowData.length}</strong> rows
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {/* ── AG Grid ── */}
      <Box
        sx={{
          flex: 1,
          px: 3,
          pb: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <div
            className="ag-theme-alpine"
            style={{ flex: 1, width: "100%", height: "100%", minHeight: 0 }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={searchText && searchColumn ? filteredRowData : rowData}
              columnDefs={allColumnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                flex: 1,
                minWidth: 120,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                  whiteSpace: "normal",
                  lineHeight: "1.4",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                },
              }}
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return { fontWeight: "bold", backgroundColor: "#f5f6f8" };
                }
                return null;
              }}
              pagination
              paginationPageSize={100}
              suppressAggFuncInHeader
              animateRows
              onGridReady={(params) => params.api.sizeColumnsToFit()}
              onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
              statusBar={{
                statusPanels: [
                  { statusPanel: "agTotalRowCountComponent", align: "left" },
                  { statusPanel: "agAggregationComponent", align: "right" },
                ],
              }}
            />
          </div>
        )}
      </Box>
    </Box>
  );
}

ConnectorPaymentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  dsaId: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired,
};
